import React, { useEffect, useState } from "react";
import { Loader2, Plus, Pencil, Trash2, Eye, EyeOff, MessageSquareQuote, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";

const empty = { name: "", company: "", photo: "", text: "", visible: true, order: 0 };

export default function AdminTestimonials() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null); // id or "new"
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/testimonials").then((r) => setItems(r.data.testimonials || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const openNew = () => { setForm(empty); setEditing("new"); };
  const openEdit = (t) => { setForm({ ...empty, ...t }); setEditing(t.id); };
  const close = () => { setEditing(null); setForm(empty); };

  const save = async () => {
    if (!form.name.trim() || !form.text.trim()) return toast.error("Client name and testimonial text are required.");
    setBusy(true);
    try {
      const payload = { ...form, order: Number(form.order) || 0 };
      if (editing === "new") await api.post("/admin/testimonials", payload);
      else await api.put(`/admin/testimonials/${editing}`, payload);
      toast.success("Testimonial saved.");
      close(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save."); }
    finally { setBusy(false); }
  };

  const toggleVisible = async (t) => {
    try { await api.put(`/admin/testimonials/${t.id}`, { ...t, visible: !t.visible }); load(); }
    catch { toast.error("Could not update visibility."); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete testimonial from ${t.name}?`)) return;
    try { await api.delete(`/admin/testimonials/${t.id}`); toast.success("Deleted."); load(); }
    catch { toast.error("Could not delete."); }
  };

  if (items === null) return <div className="mt-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-orange-600" /></div>;

  return (
    <div className="mt-8" data-testid="admin-testimonials">
      <div className="flex items-center justify-between gap-4 mb-5">
        <div className="flex items-center gap-2"><MessageSquareQuote className="h-5 w-5 text-orange-600" /><h2 className="font-heading text-xl text-foreground">Testimonials</h2></div>
        <Button onClick={openNew} className="rounded-sm bg-orange-500 text-white hover:bg-orange-600" data-testid="testimonial-add"><Plus className="h-4 w-4 mr-1.5" /> Add testimonial</Button>
      </div>

      {items.length === 0 ? (
        <div className="rounded-sm border border-border bg-card p-6 text-sm text-muted-foreground" data-testid="testimonials-empty">No testimonials yet. Add verified client quotes — only those marked visible appear on the homepage.</div>
      ) : (
        <div className="space-y-3">
          {items.map((t) => (
            <div key={t.id} className="flex items-start gap-4 rounded-sm border border-border bg-card p-4" data-testid={`testimonial-row-${t.id}`}>
              <span className="grid place-items-center h-10 w-10 rounded-full bg-secondary text-orange-600 shrink-0">{t.photo && /^https?:\/\//i.test(t.photo) ? <img src={t.photo} alt={t.name} className="h-10 w-10 rounded-full object-cover" /> : (t.photo || t.name[0]?.toUpperCase())}</span>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-foreground">{t.name}{t.company ? <span className="text-muted-foreground font-normal"> · {t.company}</span> : ""}</p>
                <p className="text-sm text-muted-foreground line-clamp-2 mt-0.5">{t.text}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => toggleVisible(t)} className={`grid place-items-center h-8 w-8 rounded-sm ${t.visible ? "text-green-700 hover:bg-green-50" : "text-muted-foreground hover:bg-secondary"}`} title={t.visible ? "Visible" : "Hidden"} data-testid={`testimonial-toggle-${t.id}`}>{t.visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}</button>
                <button onClick={() => openEdit(t)} className="grid place-items-center h-8 w-8 rounded-sm text-muted-foreground hover:bg-secondary hover:text-foreground" data-testid={`testimonial-edit-${t.id}`}><Pencil className="h-4 w-4" /></button>
                <button onClick={() => remove(t)} className="grid place-items-center h-8 w-8 rounded-sm text-red-600 hover:bg-red-50" data-testid={`testimonial-delete-${t.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={close} data-testid="testimonial-modal">
          <div className="w-full max-w-lg rounded-lg bg-card border border-border p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4"><h3 className="font-heading text-lg text-foreground">{editing === "new" ? "Add testimonial" : "Edit testimonial"}</h3><button onClick={close}><X className="h-5 w-5 text-muted-foreground" /></button></div>
            <div className="space-y-4">
              <div><label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Client name *</label><Input value={form.name} onChange={set("name")} className="mt-1.5 bg-background border-border" data-testid="testimonial-field-name" /></div>
              <div><label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Company (optional)</label><Input value={form.company} onChange={set("company")} className="mt-1.5 bg-background border-border" data-testid="testimonial-field-company" /></div>
              <div><label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Photo URL or emoji (optional)</label><Input value={form.photo} onChange={set("photo")} placeholder="https://… or 🧑‍💼" className="mt-1.5 bg-background border-border" data-testid="testimonial-field-photo" /></div>
              <div><label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Testimonial *</label><Textarea value={form.text} onChange={set("text")} className="mt-1.5 min-h-[110px] bg-background border-border" data-testid="testimonial-field-text" /></div>
              <div className="flex items-center justify-between gap-4">
                <div className="w-28"><label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Order</label><Input type="number" value={form.order} onChange={set("order")} className="mt-1.5 bg-background border-border" data-testid="testimonial-field-order" /></div>
                <label className="flex items-center gap-2 cursor-pointer text-sm text-foreground mt-5"><Checkbox checked={form.visible} onCheckedChange={(v) => setForm((f) => ({ ...f, visible: !!v }))} data-testid="testimonial-field-visible" /> Visible on homepage</label>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="outline" onClick={close} className="rounded-sm border-border bg-card">Cancel</Button>
              <Button onClick={save} disabled={busy} className="rounded-sm bg-orange-500 text-white hover:bg-orange-600" data-testid="testimonial-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
